import PropTypes from 'prop-types';
import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import window from 'window-or-global';
import { injectIntl } from 'react-intl-next';
import { WRM } from '@confluence-classic/confluence-wrm';
import { exportAjsRteAsGlobal } from '@confluence-classic/confluence-editor/src/global-objects/ajs-rte';
import { exportAjsRteBootstrapManagerAsGlobal } from '@confluence-classic/confluence-editor/src/global-objects/ajs-rte-bootstrapmanager';
import tinymceAdapter from '@confluence-classic/confluence-editor/src/bootstrap/tinymce-adapter';

import { TINY_SYNCHRONY_PLUGINS_LOAD_ACTION } from '@confluence/browser-metrics';
import { ExperienceActionMeasures } from '@confluence/action-measures';
import { EDITOR_LOADER } from '@confluence/wrm';

import waitForGlobal from '../../lib/wait-for-global-PLEASE-AVOID-USING';
import { initializeEditorPlugins } from '../../editor/plugins/loader';

class EditorLoaderComponent extends Component {
	static propTypes = {
		/**
		 * i18n
		 */
		intl: PropTypes.object,
		/**
		 * The id of the content we are preloading editor resources for
		 */
		contentId: PropTypes.string,
		/**
		 * Can the user edit the page?
		 */
		canEdit: PropTypes.bool,
		/**
		 * Success callback of the load function
		 */
		onSuccess: PropTypes.func,
		/**
		 * Error callback of the load function
		 */
		onError: PropTypes.func,
		/**
		 * Action to update the state and mark resources as not loaded yet
		 */
		onStartPreloadEditor: PropTypes.func,
		/**
		 * Action to mark editor resources as loaded for specific content Id
		 */
		onPreloadEditorFinish: PropTypes.func,
	};

	state = {
		loadedAdg3Styles: false,
		loaded: false,
	};

	componentDidMount() {
		this._mount = true;
		this._loadEditor(this.props);
		this._loadAdg3Styles();
	}

	shouldComponentUpdate(nextProps, nextState) {
		return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
	}

	componentDidUpdate(prevProps) {
		const { contentId } = this.props;
		if (contentId !== prevProps.contentId) {
			this._loadEditor(this.props);
		}
	}

	componentWillUnmount() {
		this._mount = false;
	}

	_loadAtlassianTinymce = () =>
		import(
			/* webpackChunkName: "loadable-atlassianconfluence-editorsrceditor" */ '@confluence-classic/confluence-editor/src/editor'
		);

	_loadAtlassianEditorPlugins = () =>
		import(
			/* webpackChunkName: "loadable-atlassianconfluence-editorsrceditor-pluginsindex" */ '@confluence-classic/confluence-editor/src/editor-plugins/index'
		);

	_loadMacroBrowserOverrides = () =>
		import(
			/* webpackChunkName: "loadable-atlassianconfluence-macro-browser-overrides" */ '@confluence-classic/confluence-macro-browser-overrides'
		);

	_loadOtherEditorFiles = () => {
		const { canEdit, onError } = this.props;

		WRM.require(EDITOR_LOADER, () => {
			waitForGlobal(
				{
					listener: 'EditorLoader.js',
					globalProperty: 'AJS.Confluence.EditorLoader',
				},
				() => {
					window.AJS.Confluence.EditorLoader.load(this._successCallback, onError, {
						editPermission: canEdit,
					});

					/**
					 * https://jira.atlassian.com/browse/CONFDEV-61491
					 * When frontend.exclude.editor.plugins.fragment is turned on
					 * Backend won't load editor-plugins.html (in editor.action).
					 * (from wr!com.atlassian.confluence.plugins.editor-loader:background-loading-editor)
					 * Frontend is responsible for loading the import dynamically.
					 *
					 * EditorLoader.load binds the event listener for "frontend.editor.plugins.resources.loaded"
					 * It is why we need to call it after window.AJS.Confluence.EditorLoader.load
					 */
					initializeEditorPlugins();

					if (this._mount) {
						this.setState({
							loaded: true,
						});
					}
				},
			);
		});
	};

	_loadEditor = (props) => {
		// Since loading order for Synchrony plugins (and others) needs to be chained,
		// we are starting the Synchrony plugin load event here.
		ExperienceActionMeasures.markMeasureStart(TINY_SYNCHRONY_PLUGINS_LOAD_ACTION);

		const { contentId, onStartPreloadEditor } = props;

		onStartPreloadEditor(contentId);

		// AJS is extended inside atlassian-editor. This should be solved at some point.
		waitForGlobal({ listener: 'EditorLoader.js', globalProperty: 'AJS' }, () => {
			// The loading order here is very important. They must be chained.
			// AJS -> TinyMCE -> Plugins -> Browsers -> Other stuff
			this._loadAtlassianTinymce()
				.then(() =>
					// TinyMce must be loaded before editor plugins
					this._loadAtlassianEditorPlugins().then((editorPluginsModule) => {
						editorPluginsModule.default(); // The actual plugins init must be manually invoked, it's not self-executing.
					}),
				)
				.then(() =>
					this._loadMacroBrowserOverrides().then((overridesModule) => {
						overridesModule.default();
					}),
				)
				.then(() => {
					this._loadOtherEditorFiles();
				});
		});
	};

	_successCallback = () => {
		const { intl, onPreloadEditorFinish, onSuccess, contentId } = this.props;
		onPreloadEditorFinish(contentId);
		typeof onSuccess === 'function' && onSuccess();
		// Make sure AJS.Rte.BootstrapManager gets defined before including quick-edit.js from the server.
		exportAjsRteAsGlobal();
		exportAjsRteBootstrapManagerAsGlobal();
		tinymceAdapter({
			locale: intl.locale,
			messages: intl.messages,
		});
	};

	_loadAdg3Styles = async () => {
		await import(
			/* webpackChunkName: "loadable-atlassianconfluence-editorsrcadg3-stylesindexcss" */ '@confluence-classic/confluence-editor/src/adg3-styles/index.css'
		);
		this.setState({ loadedAdg3Styles: true });
	};

	render() {
		if (!this.state.loadedAdg3Styles) {
			return null;
		}

		return <div data-testid={this.state.loaded ? 'tinymce-editor-loaded' : undefined} />;
	}
}

export default injectIntl(EditorLoaderComponent);
